import React, { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setActivePage } from "../store/activePage";
import { Html5Qrcode } from "html5-qrcode";
import { FaCamera, FaInfoCircle, FaExclamationTriangle, FaSync, FaTimes } from "react-icons/fa";
import { SuccessToast, ErrorToast } from "../components/toastify";

function ScanQrCode() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const qrCodeRef = useRef(null);
  const [cameraPermission, setCameraPermission] = useState("checking"); // 'checking', 'granted', 'denied'
  const [cameras, setCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState("");
  const [isScanning, setIsScanning] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [scanResult, setScanResult] = useState("");

  useEffect(() => {
    dispatch(
      setActivePage({
        headerName: "Scan QR Code",
        activeMenu: "scanQr",
      })
    );
  }, [dispatch]);

  // Camera list and permission are requested dynamically when the scanner starts.

  const handleScanSuccess = useCallback(async (decodedText, scanner) => {
    // Attempt parsing event ID from the QR code contents
    let eventId = "";
    
    try {
      if (/^\d+$/.test(decodedText.trim())) {
        eventId = decodedText.trim();
      } else {
        const url = new URL(decodedText, window.location.origin);
        eventId = url.searchParams.get("event_id");
      }
    } catch (e) {
      // Fallback extraction
      const match = decodedText.match(/[?&]event_id=(\d+)/);
      if (match) {
        eventId = match[1];
      }
    }

    if (eventId) {
      // Stop scanner before navigating to avoid play interruption when DOM elements are removed
      if (scanner && scanner.isScanning) {
        try {
          await scanner.stop();
        } catch (err) {
          console.error("Error stopping scanner on success:", err);
        }
      }

      SuccessToast("Event QR Code scanned successfully!");
      // Check if user is logged in
      const token = localStorage.getItem("dwt-cust-tok");
      if (token) {
        navigate(`/dashboard/create-token?event_id=${eventId}`);
      } else {
        navigate(`/quick-token?event_id=${eventId}`);
      }
    } else {
      setScanResult(decodedText);
      ErrorToast("Invalid QR format. It must contain a valid Event ID.");
    }
  }, [navigate]);

  // Handle start/stop camera scanner
  useEffect(() => {
    const qrCodeId = "reader";
    const scanner = new Html5Qrcode(qrCodeId);
    qrCodeRef.current = scanner;

    let isMounted = true;

    const startScanner = async () => {
      try {
        setErrorMsg("");
        setIsScanning(true);
        setCameraPermission("checking");

        // Try to start with environment camera by default
        let cameraConfig = { facingMode: "environment" };

        await scanner.start(
          cameraConfig,
          {
            fps: 15,
            qrbox: (width, height) => {
              const minDimension = Math.min(width, height);
              const boxSize = Math.floor(minDimension * 0.7);
              return { width: boxSize, height: boxSize };
            },
          },
          (decodedText) => {
            handleScanSuccess(decodedText, scanner);
          },
          (errorMessage) => {
            // Ignore frame decode errors
          }
        );

        if (!isMounted || document.hidden) {
          try {
            await scanner.stop();
            if (isMounted) setIsScanning(false);
          } catch (e) {}
          return;
        }

        if (isMounted) {
          setCameraPermission("granted");
          setIsScanning(true);

          // Query cameras list in the background to populate switcher
          try {
            const devices = await Html5Qrcode.getCameras();
            if (devices && devices.length > 0 && isMounted) {
              setCameras(devices);
              const backCamera = devices.find((device) =>
                device.label.toLowerCase().includes("back") ||
                device.label.toLowerCase().includes("rear") ||
                device.label.toLowerCase().includes("environment")
              );
              setSelectedCameraId(backCamera ? backCamera.id : devices[0].id);
            }
          } catch (camErr) {
            console.warn("Error getting cameras in background:", camErr);
          }
        }
      } catch (err) {
        console.error("Failed to start scanner:", err);

        const errStr = String(err).toLowerCase();
        const isPermissionError =
          errStr.includes("permission") ||
          errStr.includes("notallowed") ||
          errStr.includes("denied");

        // Fallback to first available camera if constraint fails
        if (!isPermissionError && isMounted) {
          try {
            const devices = await Html5Qrcode.getCameras();
            if (devices && devices.length > 0 && isMounted) {
              setCameras(devices);
              const firstCameraId = devices[0].id;
              setSelectedCameraId(firstCameraId);
              setCameraPermission("granted");

              // Start the fallback camera
              await scanner.start(
                firstCameraId,
                {
                  fps: 15,
                  qrbox: (width, height) => {
                    const minDimension = Math.min(width, height);
                    const boxSize = Math.floor(minDimension * 0.7);
                    return { width: boxSize, height: boxSize };
                  },
                },
                (decodedText) => {
                  handleScanSuccess(decodedText, scanner);
                },
                (errorMessage) => {}
              );

              if (!isMounted || document.hidden) {
                try {
                  await scanner.stop();
                  if (isMounted) setIsScanning(false);
                } catch (e) {}
                return;
              }

              return;
            }
          } catch (fallbackErr) {
            console.error("Fallback getCameras failed:", fallbackErr);
          }
        }

        if (isMounted) {
          setErrorMsg(
            isPermissionError
              ? "Camera permission is required to scan QR codes."
              : "Failed to start the camera. Please make sure no other app is using it."
          );
          setCameraPermission("denied");
          setIsScanning(false);
        }
      }
    };

    startScanner();

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (scanner && scanner.isScanning) {
          scanner.stop().then(() => {
            if (isMounted) setIsScanning(false);
          }).catch(() => {});
        }
      } else {
        if (isMounted && !scanner.isScanning) {
          startScanner();
        }
      }
    };
    
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isMounted = false;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      try {
        if (scanner && scanner.isScanning) {
          scanner.stop().catch((e) => {
            console.log("Cleanup stop error:", e);
          });
        }
      } catch (err) {
        console.error("Scanner stop check crashed on cleanup:", err);
      }
    };
  }, [handleScanSuccess]);

  const switchCamera = async () => {
    if (cameras.length <= 1) return;
    const currentIndex = cameras.findIndex((c) => c.id === selectedCameraId);
    const nextIndex = (currentIndex + 1) % cameras.length;
    const nextCameraId = cameras[nextIndex].id;
    
    setSelectedCameraId(nextCameraId);

    if (qrCodeRef.current && qrCodeRef.current.isScanning) {
      try {
        setIsScanning(false);
        await qrCodeRef.current.stop();
        
        setErrorMsg("");
        setIsScanning(true);
        await qrCodeRef.current.start(
          nextCameraId,
          {
            fps: 15,
            qrbox: (width, height) => {
              const minDimension = Math.min(width, height);
              const boxSize = Math.floor(minDimension * 0.7);
              return { width: boxSize, height: boxSize };
            },
          },
          (decodedText) => {
            handleScanSuccess(decodedText, qrCodeRef.current);
          },
          (errorMessage) => {}
        );
      } catch (err) {
        console.error("Failed to switch camera:", err);
        setErrorMsg("Failed to switch camera.");
        setIsScanning(false);
      }
    }
  };

  return (
    <>
      <style>{`
        @keyframes scan-laser {
          0% { transform: translateY(0); }
          50% { transform: translateY(180px); }
          100% { transform: translateY(0); }
        }
        .scan-laser-line {
          animation: scan-laser 2.5s infinite linear;
        }
      `}</style>

      <div className="max-w-md mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] overflow-hidden">
          <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-slate-800">Scan QR Code</h1>
              <p className="text-xs text-slate-500 mt-1">
                Scan an event QR code to instantly create a token
              </p>
            </div>
            <button
              onClick={async () => {
                if (qrCodeRef.current && qrCodeRef.current.isScanning) {
                  try {
                    await qrCodeRef.current.stop();
                  } catch (err) {
                    console.error("Error stopping scanner on cancel:", err);
                  }
                }
                const token = localStorage.getItem("dwt-cust-tok");
                if (token) {
                  navigate("/dashboard/home");
                } else {
                  navigate("/login");
                }
              }}
              className="p-2 rounded-xl bg-white hover:bg-slate-100 border border-slate-200 text-slate-500 hover:text-slate-800 transition"
              title="Cancel"
            >
              <FaTimes size={14} />
            </button>
          </div>

          <div className="p-6 flex flex-col items-center">
            {/* Live Camera Wrapper */}
            <div className="relative w-full aspect-square max-w-[280px] rounded-2xl overflow-hidden bg-slate-950 border border-slate-800 flex items-center justify-center">
              
              {/* HTML5 QR Code target reader container */}
              <div id="reader" className="w-full h-full overflow-hidden [&>video]:object-cover [&>video]:w-full [&>video]:h-full" />

              {/* Overlay elements when camera is active */}
              {isScanning && (
                <div className="absolute inset-0 pointer-events-none flex flex-col justify-between p-6">
                  {/* Four Corner Target Border Marks */}
                  <div className="absolute inset-[30px] border-2 border-transparent">
                    {/* Corners */}
                    <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-amber-500 rounded-tl-md" />
                    <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-amber-500 rounded-tr-md" />
                    <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-amber-500 rounded-bl-md" />
                    <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-amber-500 rounded-br-md" />
                    
                    {/* Laser line inside the box */}
                    <div className="absolute left-0 right-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-amber-500 to-transparent shadow-[0_0_8px_#d97706] scan-laser-line" />
                  </div>
                </div>
              )}

              {/* Camera access / error screens */}
              {!isScanning && cameraPermission === "checking" && (
                <div className="absolute inset-0 bg-slate-900 flex flex-col items-center justify-center p-6 text-center text-slate-400">
                  <div className="w-12 h-12 rounded-full border-4 border-amber-500/20 border-t-amber-500 animate-spin mb-4" />
                  <span className="text-xs font-bold">Requesting camera access...</span>
                </div>
              )}

              {!isScanning && cameraPermission === "denied" && (
                <div className="absolute inset-0 bg-slate-900 flex flex-col items-center justify-center p-6 text-center text-slate-400 space-y-3">
                  <FaExclamationTriangle className="text-amber-500 text-3xl" />
                  <p className="text-xs font-semibold text-slate-300">
                    {errorMsg || "Camera access denied."}
                  </p>
                  <p className="text-[10px] text-slate-500">
                    Please enable camera permissions in your browser settings to scan.
                  </p>
                </div>
              )}
            </div>

            {/* Error notifications */}
            {errorMsg && cameraPermission === "granted" && (
              <div className="w-full mt-4 flex gap-2.5 items-start bg-rose-50 border border-rose-100 rounded-xl p-3.5 text-xs text-rose-700">
                <FaInfoCircle className="mt-0.5 shrink-0 animate-pulse" />
                <p className="font-semibold">{errorMsg}</p>
              </div>
            )}

            {/* Camera Swap Action Button */}
            {cameras.length > 1 && (
              <button
                onClick={switchCamera}
                className="mt-6 flex items-center gap-2 px-5 py-2.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 text-xs font-bold rounded-xl transition"
              >
                <FaSync />
                <span>Switch Camera</span>
              </button>
            )}

            {/* Invalid QR details display */}
            {scanResult && (
              <div className="w-full mt-6 bg-slate-50 border border-slate-200/60 rounded-xl p-4 text-xs">
                <p className="font-black text-slate-400 uppercase tracking-wider mb-2">Scanned Result</p>
                <p className="font-mono text-slate-700 break-all select-all">{scanResult}</p>
                <p className="text-rose-600 font-bold mt-2 flex items-center gap-1.5">
                  <span>⚠️</span>
                  <span>Not a valid Event QR code format.</span>
                </p>
              </div>
            )}

            {/* User helper tip */}
            <div className="mt-6 flex gap-3 bg-amber-50/50 border border-amber-100/60 rounded-2xl p-4 w-full">
              <div className="w-10 h-10 rounded-full bg-amber-100/60 flex items-center justify-center shrink-0">
                <FaCamera className="text-amber-700 text-sm" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold text-amber-900 mb-0.5">Quick Token Creation</p>
                <p className="text-[10px] text-amber-800 leading-relaxed">
                  Position the event QR code within the framing box. The camera will automatically detect the Event ID and open the token submission screen.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ScanQrCode;
